package com.rainbowclubs.rainbow.presentation.club

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Club
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.domain.use_cases.club.GetClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.home.GetHomeDataUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

data class ClubUiState(
    val club: Club,
    val clubImageBytes: ByteArray?,
    val membershipStatus: MembershipStatus,
)

class ClubViewModel(
    private val clubId: String,
    private val getClubUseCase: GetClubUseCase,
    private val getHomeDataUseCase: GetHomeDataUseCase,
) : ViewModel() {

    sealed class ClubNavigationEvent {
        data class Home(val clubId: String) : ClubNavigationEvent()
        data class Messages(val clubId: String) : ClubNavigationEvent()
        data class More(val clubId: String) : ClubNavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<ClubUiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<ClubUiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<ClubNavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<ClubNavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchClub()
    }

    fun fetchClub() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getHomeDataUseCase(clubId)
                .onSuccess { homeResponse ->
                    val imageBytes = homeResponse.club.clubImageUrl
                        ?.takeIf { it.isNotBlank() }
                        ?.let { imageUrl ->
                            getClubUseCase.image(imageUrl).getOrNull()
                        }

                    _screenState.value = ScreenState.Content(
                        ClubUiState(
                            club = homeResponse.club,
                            clubImageBytes = imageBytes,
                            membershipStatus = homeResponse.membership?.status
                                ?: MembershipStatus.UNKNOWN,
                        )
                    )
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }

    fun navigateToHome() {
        navigationIntentChannel.trySend(ClubNavigationEvent.Home(clubId))
    }

    fun navigateToMessages() {
        navigationIntentChannel.trySend(ClubNavigationEvent.Messages(clubId))
    }

    fun navigateToMore() {
        navigationIntentChannel.trySend(ClubNavigationEvent.More(clubId))
    }
}
